import intl from "react-intl-universal";

import { ItemType } from "../../API";
import { ItemModelType, WorkspaceModelType } from "@stores/WorkspaceStore";

import { createItem } from "./createItem";
import { UndoableAction } from "../common/UndoableAction";
import { deleteItemForever, DeleteItemDependencies } from "./deleteItemForever";
import { undoableFunctionCall } from "../common/undoableFunctionCall";
import { logErrorToSentry } from "../../utils/errors/logErrorToSentry";
import { getDocumentLocalFirst } from "@stores/document/DocumentLocal";
import { CrudServices } from "../../platform/services/types/ServiceDependencies";
import {AppState} from "@stores/AppState";
import type { EditorView } from "prosemirror-view";
import { queryGqlApi } from "../../graphql/queryGqlApi";
import { duplicateCluster as duplicateClusterGQL } from "../../graphql/mutations";


export interface DuplicateItemOptions {
    workspace: WorkspaceModelType,
    appState: AppState,
    itemSource: ItemModelType,
    parentId?: string,
    index?: number,
    editorView?: EditorView | null,
}

export class UndoableDuplicate implements UndoableAction {
    private services: CrudServices;
    private options: DuplicateItemOptions;
    private executionPromise?: Promise<any>;

    constructor(services: CrudServices, options: DuplicateItemOptions) {
        this.services = services;
        this.options = options;
    }

    execute() {
        this.executionPromise = duplicateItem(this.services, this.options);
        return this.executionPromise;
    }

    async undo() {
        return this.executionPromise?.then(([duplicatedItem, duplicatedItemDB]) => {
            const deleteDependencies: DeleteItemDependencies = {
                ...this.services,
                appState: this.options.appState,
            };
            return deleteItemForever(deleteDependencies, duplicatedItem.id, this.options.workspace, undefined, true);
        })
    }
}

export async function duplicateItemUndoable(services: CrudServices, duplicateOptions: DuplicateItemOptions) {
    const undoableAction = new UndoableDuplicate(services, duplicateOptions);
    undoableFunctionCall(undoableAction, intl.get('snackbar.duplicate_item_success').d('Page duplicated')).catch(logErrorToSentry);
}

export async function duplicateItem(
    services: CrudServices,
    { workspace, appState, itemSource, parentId, index, editorView }: DuplicateItemOptions
) {
    const copyWord = intl.get('copy').d('copy');
    const type = itemSource.type as ItemType;
    const sourceName = itemSource.name
        ? itemSource.name
        : itemSource.getNamePlaceholder(appState.rootStore.currentLocale);
    const newName = `${sourceName} ${copyWord}`;

    if (type === ItemType.CLUSTER) {
        const result = await queryGqlApi(
            duplicateClusterGQL,
            {
                input: {
                    sourceId: itemSource.id,
                    parentId: parentId ?? (itemSource.parentIds.length ? itemSource.parentIds[0] : undefined),
                    index,
                    name: newName,
                },
            },
            services.dataClientService,
            services.authService,
            'pgServer'
        );
        if (!result) return;
        return [result, result];
    }

    let content: JSON | null = null;
    const selectedItemId = workspace.selectedId;
    const isDuplicatingCurrentLeaf = selectedItemId === itemSource.id;

    if (isDuplicatingCurrentLeaf && editorView?.state?.doc) {
        content = editorView.state.doc.toJSON() as JSON;
    } else {
        const document = await getDocumentLocalFirst(appState.userState!.wsServerConnection, itemSource.id);
        const rawDoc = document.doc as any;
        content = (typeof rawDoc?.toJSON === 'function' ? rawDoc.toJSON() : rawDoc) as JSON;
    }

    const [duplicatedItem, duplicatedItemDB] = await createItem(services, {
        workspace: workspace,
        appState,
        type: itemSource.type as ItemType,
        parentId: parentId ? parentId : (itemSource.parentIds.length ? itemSource.parentIds[0] : null),
        name: newName,
        content: content,
        noSelect: true,
        index,
    });

    if (duplicatedItem === null) {
        return;
    }

    return [duplicatedItem, duplicatedItemDB];
}
